import { IconButton, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  OfferRecipients,
  OfferStatus,
  OfferTypeRenew,
  Subscription,
} from '../../../modules/privateOffer/types'
import { useStyles } from './Recipients.styles'
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined'
import { isEmpty } from 'lodash'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import clsx from 'clsx'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import {
  LabraToolTip,
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { updatePrivateOfferForUser } from '../../../modules/privateOffer/action'
import { useDispatch, useSelector } from '../../../store'
import { checkEmailAndPhone } from '../../../common/utils/validateFields/ValidateFields'
import { UpdatePrivateOfferExpiration } from './UpdatePrivateOfferExpiration'
import { getRandom } from '../../../common/utils/helperFunctions'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  AgreementDurationType,
  ResellerAgreementStatus,
} from '../../../types/flyout'
import { isOfferExpired } from '../../../common/utils/isOfferExpired'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import {
  addRecipientsToolTipMessage,
  checkRecipientEmailDuplicate,
  type ErrorFields,
  type OfferRecipientWithError,
  updateResaleAuthorizationForUser,
} from './utils'

export type RecipientsProps = {
  recipients: OfferRecipients[]
  offerStatus?: OfferStatus
  timeZone: string
  offerId: string
  offerExpirationDate: string | null
  planType: PlanType
  offerSubscriptionEndDate?: string
  isResale?: boolean
  resaleStatus?: string
  awsOfferId?: string
  isDraft?: boolean
  agreementDurationType?: AgreementDurationType
  aggrementStartDate?: string | null
  type?: OfferTypeRenew
  labraSubscriptionId?: string | null
  subscription?: Subscription
}

export type { ErrorFields, OfferRecipientWithError }

export const Recipients: React.FC<RecipientsProps> = ({
  recipients,
  offerStatus,
  timeZone,
  offerId,
  offerExpirationDate,
  planType,
  offerSubscriptionEndDate,
  isResale = false,
  resaleStatus,
  awsOfferId,
  isDraft = false,
  agreementDurationType = '',
  aggrementStartDate,
  type,
  labraSubscriptionId,
  subscription,
}) => {
  const { classes } = useStyles()

  //For updating recipients
  const dispatch = useDispatch()

  const selectedCloudType = useCurrentCloudProvider()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [acceptDisabled, setAcceptDisabled] = useState<boolean>(false)
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const [recipientsWithErrorFields, setRecipientsWithErrorFields] = useState<
    OfferRecipientWithError[]
  >(() =>
    recipients.map(offerRecipient => ({
      ...offerRecipient,
      firstNameHasError: false,
      lastNameHasError: false,
      emailHasError: false,
      firstNameError: '',
      lastNameError: '',
      emailError: '',
    }))
  )

  const updateRow = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const key = event.target.id
      const value = event.target.value
      const tempRecipients = [...recipientsWithErrorFields]
      const recipientObject = { ...recipientsWithErrorFields[index] }
      const validate: ErrorFields = {} as ErrorFields
      if (key === 'firstName') {
        validate.firstNameHasError = isEmpty(value)
        validate.firstNameError = isEmpty(value)
          ? 'First Name cannot be empty'
          : ''
      } else if (key === 'lastName') {
        validate.lastNameHasError = isEmpty(value)
        validate.lastNameError = isEmpty(value)
          ? 'Last Name cannot be empty'
          : ''
      } else if (key === 'email') {
        validate.emailHasError = isEmpty(value)
        validate.emailError = isEmpty(value) ? 'Email cannot be empty' : ''
        if (!isEmpty(value)) {
          const { error, errMess } = checkEmailAndPhone('email', value)
          if (error) {
            validate.emailHasError = error
            validate.emailError = errMess
          } else if (checkRecipientEmailDuplicate(value, tempRecipients)) {
            validate.emailHasError = true
            validate.emailError = 'Recipient email must be unique'
          } else {
            validate.emailHasError = false
            validate.emailError = ''
          }
        }
      }
      const updatedRecipient = {
        ...recipientObject,
        [key]: value,
        ...validate,
      }
      tempRecipients[index] = updatedRecipient
      setRecipientsWithErrorFields(tempRecipients)
    },
    [recipientsWithErrorFields]
  )

  const deleteRow = useCallback(
    (index: number) => () => {
      const recipientsArray = [...recipientsWithErrorFields]
      recipientsArray.splice(index, 1)
      setRecipientsWithErrorFields(recipientsArray)
    },
    [recipientsWithErrorFields]
  )

  const callPatchRecipient = (
    offerId: string,
    data: Record<string, unknown>
  ) =>
    isResale
      ? updateResaleAuthorizationForUser(offerId, data)
      : updatePrivateOfferForUser(offerId, data)

  const handleClick = useCallback(() => {
    const newRow: OfferRecipientWithError = {
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      firstNameHasError: false,
      lastNameHasError: false,
      emailHasError: false,
      firstNameError: '',
      lastNameError: '',
      emailError: '',
    }
    const newRecipients = [...recipientsWithErrorFields]
    newRecipients.push(newRow)
    setRecipientsWithErrorFields(newRecipients)
  }, [recipientsWithErrorFields])

  useEffect(() => {
    updateDisabled()
  }, [recipientsWithErrorFields])

  const handleOpenModal = useCallback(() => {
    setIsOpen(true)
  }, [isOpen])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [isOpen])

  const updateDisabled = useCallback(() => {
    const hasError =
      isEmpty(recipientsWithErrorFields) ||
      recipientsWithErrorFields.some(
        recipient =>
          isEmpty(recipient.firstName) ||
          isEmpty(recipient.lastName) ||
          isEmpty(recipient.email) ||
          recipient.emailHasError
      )
    setAcceptDisabled(hasError)
  }, [recipientsWithErrorFields])

  const handleAccept = useCallback(async () => {
    setIsOpen(false)
    const offerRecipientsToSend = recipientsWithErrorFields.map(recipient => {
      const {
        firstName,
        lastName,
        email,
        title,
        crmObjectId,
        lastViewedAt,
        sentAt,
      } = recipient
      return {
        firstName,
        lastName,
        email,
        title,
        crmObjectId,
        ...(selectedCloudType === 'GCP' ? { lastViewedAt, sentAt } : {}),
      }
    })
    await dispatch(
      callPatchRecipient(offerId, {
        ...(isResale
          ? {
              status: resaleStatus,
              awsAgreementId: awsOfferId,
              agreementRecipients: offerRecipientsToSend,
            }
          : { offerRecipients: offerRecipientsToSend }),
      })
    )
  }, [recipientsWithErrorFields])

  const canShowAddRecipientBtn =
    ((!isResale || resaleStatus === ResellerAgreementStatus.ACTIVE) &&
      selectedCloudType === 'AWS') ||
    selectedCloudType === 'GCP'

  const isExpired =
    !!offerExpirationDate && isOfferExpired(new Date(offerExpirationDate))

  const isDisabledAddRecipientForOfferExpired = isResale
    ? agreementDurationType.toUpperCase() !==
        AgreementDurationType.NO_SET_TIME && isExpired
    : isExpired

  const disabledAddRecipent =
    offerStatus === OfferStatus?.PRIVATE_OFFER_FAILED ||
    isDisabledAddRecipientForOfferExpired ||
    offerStatus === OfferStatus?.PRIVATE_OFFER_RESTRCITED ||
    offerStatus === OfferStatus?.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS ||
    loading

  const shouldShowUpdateExpiration =
    (!isResale && selectedCloudType === 'AWS') || selectedCloudType === 'GCP'

  return (
    <>
      <div
        className={classes.recipientsParentContainer}
        data-testid="recipients-container"
      >
        <div className={classes.recipientsTextAndUpdateDateContainer}>
          <Typography className={classes.recipientsText}>Recipients</Typography>
          {shouldShowUpdateExpiration && (
            <UpdatePrivateOfferExpiration
              offerExpirationDate={offerExpirationDate!}
              offerId={offerId}
              planType={planType}
              offerSubscriptionEndDate={offerSubscriptionEndDate || ''}
              offerStatus={offerStatus}
              isDraft={isDraft}
              aggrementStartDate={aggrementStartDate}
              type={type}
              labraSubscriptionId={labraSubscriptionId}
              subscription={subscription}
            />
          )}
        </div>
        {recipients.map(recipient => (
          <div
            className={classes.recipientsRow}
            key={recipient.email}
            data-testid={`${recipient.email}-div`}
          >
            <div className={classes.nameIconContainer}>
              <AccountCircleOutlined
                className={classes.accountIcon}
                data-testid={`icon-${recipient.email}`}
              />
              <div className={classes.namesContainer}>
                <Typography
                  className={classes.namesText}
                  data-testid={`name-${recipient.email}`}
                >
                  {`${recipient.firstName || ''} ${recipient.lastName || ''}`.trim()}
                </Typography>
                <Typography
                  className={classes.emailsText}
                  data-testid={`email-${recipient.email}`}
                >
                  {recipient.email}
                </Typography>
              </div>
            </div>
            <ShowRecipientStatus
              recipient={recipient}
              isOfferInProgress={
                offerStatus ===
                  OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS ||
                resaleStatus === ResellerAgreementStatus.IN_PROGRESS
              }
              timeZone={timeZone}
            />
          </div>
        ))}
        <div className={classes.addButtonContainer}>
          {canShowAddRecipientBtn &&
            (disabledAddRecipent ? (
              <div className={classes.displayTooltipInAddButton}>
                <LabraToolTip
                  tooltipPlacement="top"
                  label={addRecipientsToolTipMessage}
                >
                  <AddUpdateButton
                    label={`Add another ${
                      isResale ? 'authorization' : 'offer'
                    } recipient`}
                    onClick={handleOpenModal}
                    disabled={disabledAddRecipent}
                    icon={AddCircleOutlineIcon}
                    type={'add'}
                  />
                </LabraToolTip>
              </div>
            ) : (
              <AddUpdateButton
                label={`Add another ${
                  isResale ? 'authorization' : 'offer'
                } recipient`}
                onClick={handleOpenModal}
                disabled={disabledAddRecipent}
                icon={AddCircleOutlineIcon}
                type={'add'}
              />
            ))}
        </div>
      </div>
      <ModalConfirm
        title={`Add ${isResale ? 'Authorization' : 'offer'} recipients`}
        subTitle={''}
        content={
          <AddRecipients
            offerRecipients={recipientsWithErrorFields}
            handleClick={handleClick}
            updateValue={updateRow}
            deleteRow={deleteRow}
            buttonLabel={`Add another ${
              isResale ? 'Authorization' : 'offer'
            } recipient`}
          />
        }
        open={isOpen}
        titleAcceptButton={'Update'}
        titleCancelButton={'Cancel'}
        onAccept={handleAccept}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={acceptDisabled}
      />
    </>
  )
}

export type ShowRecipientStatusProps = {
  recipient: OfferRecipients
  isOfferInProgress: boolean
  timeZone: string
}
export const ShowRecipientStatus = React.memo(
  ({ recipient, isOfferInProgress, timeZone }: ShowRecipientStatusProps) => {
    const { classes } = useStyles()
    const { modifiedDate: sentDate, modifiedTime: sentTime } =
      dateAndTimeConversion(recipient.sentAt || '', timeZone)
    const { modifiedDate: clickedDate, modifiedTime: clickedTime } =
      dateAndTimeConversion(recipient.lastViewedAt || '', timeZone)
    if (isOfferInProgress) {
      return (
        <div
          className={classes.status}
          data-testid={`in-progress-${recipient.email}`}
        >
          <Typography
            className={clsx(classes.recipientsStatus)}
            data-testid={`status-in-progress-${recipient.email}`}
          >
            In Queue
          </Typography>
        </div>
      )
    } else if (!isEmpty(recipient.lastViewedAt)) {
      return (
        <div
          className={classes.status}
          data-testid={`status-viewed-at-${recipient.email}`}
        >
          <CheckCircleOutlineIcon className={classes.checkIcon} />
          <Typography className={classes.recipientsStatus}>
            {`Viewed at ${clickedDate}`}
          </Typography>
          <Typography className={classes.statusTime}>{clickedTime}</Typography>
        </div>
      )
    } else if (!isEmpty(recipient.sentAt)) {
      return (
        <div
          className={clsx(classes.status)}
          data-testid={`status-sent-at-${recipient.email}`}
        >
          <Typography className={classes.recipientsStatus}>
            {`Sent at ${sentDate}`}
          </Typography>
          <Typography className={classes.statusTime}>{sentTime}</Typography>
        </div>
      )
    } else return <div data-testid={`empty-component-${recipient.email}`}></div>
  }
)

export type AddRecipientsProps = {
  offerRecipients: OfferRecipientWithError[]
  handleClick: () => void
  updateValue: (
    index: number
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void
  deleteRow: (index: number) => () => void
  buttonLabel: string
  isResaleAuthorization?: boolean
}
export const AddRecipients = React.memo(
  ({
    offerRecipients,
    handleClick,
    deleteRow,
    updateValue,
    buttonLabel,
    isResaleAuthorization,
  }: AddRecipientsProps) => {
    const { classes } = useStyles()
    return (
      <div
        className={classes.recipientsRowsContainer}
        data-testid="update-recipients"
      >
        {offerRecipients.map(
          (offerRecipient: OfferRecipientWithError, index: number) => (
            <OfferRecipientsRow
              index={index}
              recipientsLength={offerRecipients.length}
              offerRecipient={offerRecipient}
              update={updateValue(index)}
              deleteRow={deleteRow(index)}
              key={index}
              isResaleAuthorization={isResaleAuthorization}
            />
          )
        )}
        <div
          className={classes.addButtonContainer}
          style={{ marginTop: '16px' }}
        >
          <AddUpdateButton
            label={buttonLabel}
            onClick={handleClick}
            disabled={false}
            icon={AddCircleOutlineIcon}
            type={'add'}
          />
        </div>
      </div>
    )
  }
)
export type OfferRecipientsRowProps = {
  index: number
  recipientsLength: number
  offerRecipient: OfferRecipientWithError
  deleteRow: () => void
  update: (event: React.ChangeEvent<HTMLInputElement>) => void
  isResaleAuthorization?: boolean
}
export const OfferRecipientsRow = React.memo(
  ({
    index,
    recipientsLength,
    offerRecipient,
    update,
    deleteRow,
    isResaleAuthorization,
  }: OfferRecipientsRowProps) => {
    const { classes } = useStyles()
    const randomKey = useMemo(getRandom, [index, recipientsLength])
    return (
      <div
        className={classes.updateRow}
        data-testid="recipient-row"
        key={`${randomKey}-${index}`}
      >
        <TextField
          id="firstName"
          label="First name"
          variant="standard"
          required={true}
          key={'firstName'}
          defaultValue={offerRecipient.firstName}
          className={classes.textField}
          onChange={update}
          error={offerRecipient.firstNameHasError}
          helperText={
            offerRecipient.firstNameHasError
              ? offerRecipient.firstNameError
              : ' '
          }
          slotProps={{
            input: { inputProps: { 'data-testid': 'firstname' } },
          }}
        />
        <TextField
          id="lastName"
          label="Last name"
          variant="standard"
          required={true}
          defaultValue={offerRecipient.lastName}
          className={classes.textField}
          onChange={update}
          error={offerRecipient.lastNameHasError}
          helperText={
            offerRecipient.lastNameHasError ? offerRecipient.lastNameError : ' '
          }
          slotProps={{
            input: { inputProps: { 'data-testid': 'lastname' } },
          }}
        />
        <TextField
          id="title"
          label="Title"
          variant="standard"
          defaultValue={offerRecipient.title}
          className={classes.textField}
          onChange={update}
          helperText={' '}
          slotProps={{
            input: { inputProps: { 'data-testid': 'title' } },
          }}
        />
        <TextField
          id="email"
          label="Email"
          variant="standard"
          required={true}
          type="email"
          defaultValue={offerRecipient.email}
          className={classes.textField}
          onChange={update}
          error={offerRecipient.emailHasError}
          helperText={
            offerRecipient.emailHasError ? offerRecipient.emailError : ' '
          }
          slotProps={{
            input: { inputProps: { 'data-testid': 'email' } },
          }}
        />
        <IconButton
          className={classes.closeIconButton}
          onClick={deleteRow}
          data-testid="cancel-button"
          size="large"
        >
          {!isResaleAuthorization ? (
            <HighlightOffIcon className={classes.closeIcon} />
          ) : (
            <CancelIcon fontSize="small" />
          )}
        </IconButton>
      </div>
    )
  }
)
